import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { MessageSquare, Loader2, Send, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import { formatApiError } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full p-2.5 rounded-xl bg-soft border border-transparent focus:border-mint focus:outline-none text-sm text-forest";

export function SmsStatusPill({ status }) {
  const colours = {
    queued: "bg-soft text-forest",
    sent: "bg-mint/20 text-forest",
    delivered: "bg-lime text-forest",
    received: "bg-blue-100 text-blue-800",
    failed: "bg-red-100 text-red-800",
  };
  return (
    <span className={"rounded-full px-2.5 py-0.5 text-[11px] font-bold " + (colours[status] || "bg-soft text-forest")}>
      {status}
    </span>
  );
}

export function formatSmsTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function SmsSection({ relatedType, relatedId, phone }) {
  const [messages, setMessages] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [body, setBody] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState({ error: "", ok: "" });

  const load = useCallback(async () => {
    try {
      const [m, t] = await Promise.all([
        axios.get(`${API}/admin/sms/messages`, { params: { relatedType, relatedId } }),
        axios.get(`${API}/admin/sms/templates`),
      ]);
      setMessages(m.data.messages);
      setTemplates(t.data.templates);
    } catch (err) {
      setMsg({ error: formatApiError(err), ok: "" });
    }
  }, [relatedType, relatedId]);

  useEffect(() => { load(); }, [load]);

  const pickTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) setBody(t.body);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    setMsg({ error: "", ok: "" });
    try {
      await axios.post(`${API}/admin/sms/send`, { relatedType, relatedId, body: body.trim() });
      setBody("");
      setTemplateId("");
      setMsg({ error: "", ok: "Message sent." });
      await load();
    } catch (err) {
      setMsg({ error: formatApiError(err), ok: "" });
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid={`sms-section-${relatedType}`} className="rounded-2xl bg-white border border-border-neutral p-6">
      <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
        <MessageSquare className="h-4 w-4 text-mint" /> SMS
        {phone && <span className="text-xs font-semibold text-mid-neutral">to {phone}</span>}
      </h2>

      <form onSubmit={send} className="mt-4 space-y-3">
        <select
          value={templateId}
          onChange={(e) => pickTemplate(e.target.value)}
          data-testid={`sms-template-${relatedType}`}
          className={inputCls}
        >
          <option value="">Insert a template…</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div>
          <textarea
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type your message…"
            data-testid={`sms-body-${relatedType}`}
            className={inputCls}
          />
          <p className="mt-1 text-right text-[11px] text-mid-neutral">{body.length} / 1600</p>
        </div>
        {msg.error && <p data-testid={`sms-error-${relatedType}`} className="text-sm text-red-700">{msg.error}</p>}
        {msg.ok && <p data-testid={`sms-ok-${relatedType}`} className="text-sm font-semibold text-forest">{msg.ok}</p>}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!body.trim() || sending}
            data-testid={`sms-send-${relatedType}`}
            className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Send SMS
          </button>
        </div>
      </form>

      <div className="mt-5 space-y-2">
        {messages === null && (
          <p className="flex items-center text-sm text-mid-neutral"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading messages…</p>
        )}
        {messages && messages.length === 0 && (
          <p className="text-sm text-mid-neutral">No messages yet.</p>
        )}
        {(messages || []).map((m) => (
          <div key={m.id} data-testid={`sms-msg-${m.id}`} className="flex items-start gap-3 rounded-xl bg-soft/50 p-3">
            <div className={"mt-0.5 h-7 w-7 rounded-full inline-flex items-center justify-center shrink-0 " +
              (m.direction === "outbound" ? "bg-forest text-lime" : "bg-blue-100 text-blue-800")}>
              {m.direction === "outbound" ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownLeft className="h-3.5 w-3.5" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-forest whitespace-pre-wrap">{m.body}</p>
              <p className="mt-1 text-xs text-mid-neutral">
                {m.direction === "outbound" ? `by ${m.sentByName || m.sentBy || "—"}` : `from ${m.fromNumber}`}
                {" · "}{formatSmsTs(m.createdAt)}
              </p>
            </div>
            <SmsStatusPill status={m.status} />
          </div>
        ))}
      </div>
    </section>
  );
}
